import ErrorToast from "../../components/common/toasts/ErrorToast";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import usePremiumResource from "./hooks/usePremiumResource";
import ProgressBar from "../../components/common/ProgressBar";
import { OrderStatus, PreviewKind } from "./constants";
import { premiumPaths } from "./navigation";
import PremiumLayout from "./components/PremiumLayout";
import SourcePreview from "./components/sources/SourcePreview";
import { sourceContext } from "./sources";
import OrderSteps, {
  getOrderStep,
  orderStepPaths,
} from "./components/OrderSteps";
import OrderFilePreview from "./components/OrderFilePreview";
import OrderActions from "./components/OrderActions";
import PremiumBackLink from "./components/PremiumBackLink";
import { useToast } from "../../components/common/toasts/ToastContext";
import useAsyncAction from "./hooks/useAsyncAction";
import saveOrderDraft from "./saveOrderDraft";

/** Osobny podgląd odtwarza dokument po odświeżeniu i wraca do zapisanego zlecenia. */
export default function DocumentPreview({ kind }) {
  const { orderId, documentId } = useParams();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnToDocuments =
    kind === PreviewKind.SOURCE && params.get("from") === "documents";
  const showSource =
    kind === PreviewKind.SOURCE &&
    (params.has("item") || params.has("recipient"));
  const {
    data,
    error: loadError,
    replace,
  } = usePremiumResource(accessToken, `/orders/${orderId}`);
  const order = data?.order;
  const attachment = order?.documents.find((doc) => doc.id === documentId);
  const error =
    loadError ||
    (order && kind === PreviewKind.SOURCE && !attachment
      ? "Nie znaleziono dokumentu w tym zleceniu."
      : "");
  const [readyRevision, setReadyRevision] = useState(null);
  const previewKey = order && `${order.id}:${order.revision}`;
  const previewReady = Boolean(order && readyRevision === previewKey);
  const { notify } = useToast();
  const saving = useAsyncAction(`${accessToken}:${orderId}:${kind}`);
  const savePreview = (advance) =>
    saving.run(async (isActive) => {
      const updated = await saveOrderDraft(accessToken, order);
      if (!isActive()) return;
      replace({ order: updated });
      if (updated.previewRevision !== updated.revision) {
        setReadyRevision(null);
      } else if (advance) {
        navigate(premiumPaths.issue(orderId));
        return;
      }
      notify("Zapisano wersję roboczą.");
    });
  useEffect(() => {
    if (
      kind === PreviewKind.DRAFT &&
      order &&
      order.status !== OrderStatus.REVIEW
    ) {
      navigate(premiumPaths.order(orderId), { replace: true });
    }
  }, [order, orderId, kind, navigate]);
  const context = showSource && order ? sourceContext(order, params) : null;
  const title =
    kind === PreviewKind.SOURCE
      ? "Dokument źródłowy"
      : kind === PreviewKind.CERTIFICATE
        ? "Gotowe świadectwo"
        : "Podgląd świadectwa";
  return (
    <PremiumLayout
      title={title}
      subtitle={order?.config.exporter.split("\n")[0]}
      actions={
        kind === PreviewKind.DRAFT && order?.organizationId ? (
          <PremiumBackLink
            to={premiumPaths.orders(order.organizationId)}
            destination="Lista świadectw"
          />
        ) : (
          <PremiumBackLink
            to={
              returnToDocuments
                ? premiumPaths.orderDocuments(orderId)
                : `${premiumPaths.order(orderId)}${context?.returnHash ? `#${context.returnHash}` : ""}`
            }
            destination={
              returnToDocuments
                ? "Dokumenty źródłowe"
                : kind === PreviewKind.CERTIFICATE
                  ? "Zlecenie"
                  : "Dane świadectwa"
            }
          />
        )
      }
    >
      <OrderSteps
        paths={orderStepPaths(
          order,
          kind === PreviewKind.DRAFT ? { previewReady } : {},
        )}
        step={
          returnToDocuments
            ? 1
            : kind === PreviewKind.DRAFT
              ? 3
              : kind === PreviewKind.CERTIFICATE
                ? 4
                : getOrderStep(order?.status)
        }
      />
      <ErrorToast message={error || saving.error} />
      {!order && !error && (
        <div className="card premium-panel">
          <ProgressBar label="Przygotowanie podglądu" />
        </div>
      )}
      {kind === PreviewKind.SOURCE && attachment && !showSource && (
        <OrderFilePreview
          path={`/orders/${orderId}/documents/${encodeURIComponent(documentId)}`}
          fileName={attachment.name}
          downloadLabel="Pobierz dokument"
        />
      )}
      {kind === PreviewKind.CERTIFICATE && order && (
        <OrderFilePreview
          path={`/orders/${orderId}/certificate`}
          fileName="Świadectwo.pdf"
          downloadLabel="Pobierz świadectwo"
        />
      )}
      {kind === PreviewKind.DRAFT && order?.status === OrderStatus.REVIEW && (
        <OrderFilePreview
          path={`/orders/${orderId}/preview`}
          revision={order.revision}
          onReadyChange={(ready) => {
            setReadyRevision(ready ? previewKey : null);
            if (ready && order.previewRevision !== order.revision)
              replace({ order: { ...order, previewRevision: order.revision } });
          }}
        />
      )}
      {kind === PreviewKind.DRAFT &&
        order?.status === OrderStatus.REVIEW &&
        previewReady &&
        !error && (
          <OrderActions
            backTo={premiumPaths.order(orderId)}
            backLabel="Dane świadectwa"
            busy={saving.busy}
            save={() => savePreview(false)}
            onNext={() => savePreview(true)}
          />
        )}
      {showSource &&
        order &&
        !error &&
        (context ? (
          <SourcePreview
            order={order}
            context={context}
            token={accessToken}
            documentId={documentId}
          />
        ) : (
          <p>Nie znaleziono źródła tych danych. Wróć do danych świadectwa.</p>
        ))}
    </PremiumLayout>
  );
}
